import {
  faFile,
  faFilePdf,
  faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Button, Modal, Spinner } from "react-bootstrap";

export interface GroupPaymentRow {
  _id: string;
  student?: any;
  examGroup?: any;
  isPaid?: boolean;
  paymentMode?: string;
  approvalStatus?: string;
  paymentProof?: string;
  paymentProofName?: string;
  requestedAt?: string;
  approvedAt?: string;
  updatedAt?: string;
}

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

function extensionOf(value?: string) {
  return String(value || "").split("?")[0].split(".").pop()?.toLowerCase() || "";
}

/** Prefer the original filename for type detection, falling back to the stored URL. */
function proofKind(payment: GroupPaymentRow): "image" | "pdf" | "unknown" {
  for (const candidate of [payment.paymentProofName, payment.paymentProof]) {
    const ext = extensionOf(candidate);
    if (IMAGE_EXTENSIONS.indexOf(ext) !== -1) return "image";
    if (ext === "pdf") return "pdf";
  }
  return "unknown";
}

/**
 * Proof of payment the candidate attached — a thumbnail (images) or file-name button
 * that opens the full-screen viewer. Deliberately not an <a href>, so clicking it
 * previews the file rather than downloading it.
 */
export function GroupPaymentProof({
  payment,
  onView,
}: {
  payment: GroupPaymentRow;
  onView: (payment: GroupPaymentRow) => void;
}) {
  if (!payment.paymentProof) {
    return <span className="text-muted fs-12">No proof</span>;
  }

  const kind = proofKind(payment);

  return (
    <button
      type="button"
      className="btn btn-link p-0 d-inline-flex align-items-center gap-2 text-decoration-none"
      title={payment.paymentProofName || "View proof of payment"}
      onClick={() => onView(payment)}
    >
      {kind === "image" ? (
        <img
          src={payment.paymentProof}
          alt="Proof of payment"
          style={{
            width: 40,
            height: 40,
            objectFit: "cover",
            borderRadius: 4,
            border: "1px solid #dee2e6",
          }}
        />
      ) : (
        <span className="xrg-table-icon">
          <FontAwesomeIcon icon={kind === "pdf" ? faFilePdf : faFile} />
        </span>
      )}
      <span className="fs-12 text-truncate" style={{ maxWidth: 140 }}>
        {payment.paymentProofName || "View proof"}
      </span>
    </button>
  );
}

/**
 * Full-screen proof viewer. Images render inline; PDFs render in an embedded frame.
 * Both also offer "Open in new tab" as a fallback.
 */
export function GroupPaymentProofModal({
  payment,
  onHide,
}: {
  payment: GroupPaymentRow | null;
  onHide: () => void;
}) {
  const kind = payment ? proofKind(payment) : "unknown";
  const candidateName = payment?.student
    ? `${payment.student.name || ""} ${payment.student.last_name || ""}`.trim()
    : "";

  return (
    <Modal show={!!payment} onHide={onHide} fullscreen scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-bold">
          Proof of Payment
          {candidateName ? <span className="text-muted fw-normal"> — {candidateName}</span> : null}
          {payment?.paymentProofName ? (
            <div className="text-muted fw-normal fs-12">{payment.paymentProofName}</div>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light d-flex flex-column p-0">
        {!payment?.paymentProof ? (
          <div className="text-center text-muted py-5">No proof uploaded</div>
        ) : kind === "image" ? (
          <div className="d-flex align-items-center justify-content-center flex-grow-1 p-3">
            <img
              src={payment.paymentProof}
              alt="Proof of payment"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          </div>
        ) : kind === "pdf" ? (
          <iframe
            src={payment.paymentProof}
            title="Proof of payment"
            style={{ border: "none", width: "100%", flexGrow: 1, minHeight: "70vh" }}
          />
        ) : (
          <div className="text-center py-5">
            <p className="text-muted mb-3">
              This file is not an image or a PDF, so it cannot be previewed here.
            </p>
            <a
              href={payment.paymentProof}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary btn-sm"
            >
              Open in new tab
            </a>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {payment?.paymentProof && (
          <a
            href={payment.paymentProof}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-secondary btn-sm"
          >
            <FontAwesomeIcon icon={faUpRightFromSquare} className="me-2" />
            Open in new tab
          </a>
        )}
        <Button variant="light" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const STATUS_VARIANT: Record<string, string> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "danger",
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: "Awaiting Approval",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

/** Status badge for a group payment row — falls back to the plain paid/unpaid state. */
export function GroupPaymentStatusBadge({ payment }: { payment: GroupPaymentRow }) {
  const status = payment.approvalStatus || "";

  if (STATUS_VARIANT[status]) {
    return <Badge bg={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>;
  }

  return (
    <Badge bg={payment.isPaid ? "success" : "secondary"}>
      {payment.isPaid ? "Paid" : "Unpaid"}
    </Badge>
  );
}

export function GroupPaymentActions({
  payment,
  busy,
  onApprove,
  onReject,
}: {
  payment: GroupPaymentRow;
  busy: boolean;
  onApprove: (paymentId: string) => void;
  onReject: (paymentId: string) => void;
}) {
  const status = payment.approvalStatus;

  if (status !== "PENDING" && status !== "APPROVED" && status !== "REJECTED") {
    return <span className="text-muted fs-12">—</span>;
  }

  const spinner = <Spinner animation="border" size="sm" />;

  if (status === "APPROVED") {
    return (
      <div className="d-flex justify-content-center">
        <Button
          variant="outline-danger"
          size="sm"
          disabled={busy}
          title="Undo this approval. The candidate keeps their saved progress but cannot continue until it is approved again."
          onClick={() => onReject(payment._id)}
        >
          {busy ? spinner : "Revoke Approval"}
        </Button>
      </div>
    );
  }

  if (status === "REJECTED") {
    return (
      <div className="d-flex justify-content-center">
        <Button
          variant="success"
          size="sm"
          disabled={busy}
          title="Approve this payment after all, restoring the candidate's access."
          onClick={() => onApprove(payment._id)}
        >
          {busy ? spinner : "Approve"}
        </Button>
      </div>
    );
  }

  return (
    <div className="d-flex gap-2 justify-content-center">
      <Button
        variant="success"
        size="sm"
        disabled={busy}
        onClick={() => onApprove(payment._id)}
      >
        {busy ? spinner : "Approve"}
      </Button>
      <Button
        variant="outline-danger"
        size="sm"
        disabled={busy}
        onClick={() => onReject(payment._id)}
      >
        Reject
      </Button>
    </div>
  );
}
